import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { eventsApi } from '../services/eventsApi';
import { useAuth } from '../hooks/useAuth';
import LoginSignupModal from './modals/LoginSignupModal';
import { roomTaxesFromDiscounted } from '../utils/roomPricing';

/**
 * @param {string} resolvedEventId — event id from URL (page) or parent (modal)
 * @param {object | null} [initialEvent] — optional preloaded stay/event object
 * @param {'page' | 'modal'} [layout]
 * @param {() => void} [onRequestClose] — modal: back / close; page: ignored
 */
export function TicketSelectionPanel({
  resolvedEventId,
  initialEvent = null,
  layout = 'page',
  onRequestClose
}) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const isModal = layout === 'modal';

  const [event, setEvent] = useState(initialEvent || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [availableDates, setAvailableDates] = useState([]);
  const [availableShows, setAvailableShows] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedShow, setSelectedShow] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [hasSelectedDates, setHasSelectedDates] = useState(false);
  const [ticketQuantities, setTicketQuantities] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Fetch event data and available dates/shows
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        setError(null);

        const eventId = resolvedEventId;

        if (!eventId) {
          throw new Error('No event ID provided. Please select an event first.');
        }

        let eventData = initialEvent || null;
        if (!eventData?.title) {
          const eventResponse = await eventsApi.getEventById(eventId);
          eventData = eventResponse.event || eventResponse;
        }
        setEvent(eventData);

        const datesResponse = await eventsApi.getEventDates(eventId);
        
        if (!datesResponse || datesResponse.length === 0) {
          throw new Error('No dates available for this event. Please add shows in the admin panel.');
        }
        
        setAvailableDates(datesResponse);

        // Set default selected date to first available date
        if (datesResponse.length > 0) {
          const firstDate = datesResponse[0];
          setSelectedDate(firstDate);
          setSelectedMonth(new Date(firstDate.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
          
          const showsResponse = await eventsApi.getEventShows(eventId, firstDate.date);
          setAvailableShows(showsResponse);
          
          // Set default selected show
          if (showsResponse.length > 0) {
            const firstShow = showsResponse[0];
            setSelectedShow(firstShow);
            setHasSelectedDates(true);
            
            const ticketsResponse = await eventsApi.getTicketTypes(eventId, firstShow.showId);
            setTicketTypes(ticketsResponse);
            
            // Initialize ticket quantities
            const initialQuantities = {};
            ticketsResponse.forEach(ticket => {
              initialQuantities[ticket.ticketTypeId] = 0;
            });
            // Set first ticket to quantity 1 by default
            if (ticketsResponse.length > 0) {
              initialQuantities[ticketsResponse[0].ticketTypeId] = 1;
            }
            setTicketQuantities(initialQuantities);
          }
        }

      } catch (err) {
        console.error('❌ Error fetching event data:', err);
        console.error('❌ Error details:', {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status
        });
        setError(err.message || 'Failed to load event data');
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [resolvedEventId, initialEvent]);

  // Handle date selection
  const handleDateSelect = async (dateInfo) => {
    try {
      setSelectedDate(dateInfo);
      setSelectedShow(null);
      setTicketTypes([]);
      
      // Fetch shows for selected date
      const eventId = resolvedEventId || event?.id || event?.eventId;
      const showsResponse = await eventsApi.getEventShows(eventId, dateInfo.date);
      setAvailableShows(showsResponse);
      
      // Auto-select first show if available
      if (showsResponse.length > 0) {
        const firstShow = showsResponse[0];
        setSelectedShow(firstShow);
        
        // Fetch ticket types for the selected show
        const ticketsResponse = await eventsApi.getTicketTypes(eventId, firstShow.showId);
        setTicketTypes(ticketsResponse);
        
        // Reset ticket quantities
        const initialQuantities = {};
        ticketsResponse.forEach(ticket => {
          initialQuantities[ticket.ticketTypeId] = 0;
        });
        if (ticketsResponse.length > 0) {
          initialQuantities[ticketsResponse[0].ticketTypeId] = 1;
        }
        setTicketQuantities(initialQuantities);
      }
    } catch (err) {
      console.error('Error fetching shows for date:', err);
    }
  };

  // Handle show selection
  const handleShowSelect = async (show) => {
    try {
      setSelectedShow(show);
      
      // Fetch ticket types for selected show
      const eventId = resolvedEventId || event?.id || event?.eventId;
      const ticketsResponse = await eventsApi.getTicketTypes(eventId, show.showId);
      setTicketTypes(ticketsResponse);
      
      // Reset ticket quantities
      const initialQuantities = {};
      ticketsResponse.forEach(ticket => {
        initialQuantities[ticket.ticketTypeId] = 0;
      });
      if (ticketsResponse.length > 0) {
        initialQuantities[ticketsResponse[0].ticketTypeId] = 1;
      }
      setTicketQuantities(initialQuantities);
    } catch (err) {
      console.error('Error fetching tickets for show:', err);
    }
  };

  const updateQuantity = (ticketId, change) => {
    setTicketQuantities(prev => ({
      ...prev,
      [ticketId]: Math.max(0, prev[ticketId] + change)
    }));
  };

  const handleAddTicket = (ticketId) => {
    setTicketQuantities(prev => ({
      ...prev,
      [ticketId]: prev[ticketId] + 1
    }));
  };

  const handleRemoveTicket = (ticketId) => {
    setTicketQuantities(prev => ({
      ...prev,
      [ticketId]: Math.max(0, prev[ticketId] - 1)
    }));
  };

  const handleBack = () => {
    if (onRequestClose) onRequestClose();
    else navigate(-1);
  };

  // Check if user has selected any tickets
  const hasSelectedTickets = () => {
    return Object.values(ticketQuantities).some(quantity => quantity > 0);
  };

  // Calculate total price
  const calculateTotalPrice = () => {
    let subTotal = 0;
    let taxes = 0;
    
    ticketTypes.forEach(ticket => {
      const quantity = ticketQuantities[ticket.ticketTypeId] || 0;
      if (quantity > 0) {
        const ticketPrice = ticket.price?.amount || 0;
        const ticketTaxes = roomTaxesFromDiscounted(ticketPrice);
        
        subTotal += ticketPrice * quantity;
        taxes += ticketTaxes * quantity;
      }
    });
    
    return {
      subTotal,
      taxes,
      total: subTotal
    };
  };

  // Get selected tickets with details
  const getSelectedTickets = () => {
    return ticketTypes
      .filter(ticket => (ticketQuantities[ticket.ticketTypeId] || 0) > 0)
      .map(ticket => ({
        id: ticket.ticketTypeId,
        name: ticket.name || ticket.title,
        description: ticket.description,
        quantity: ticketQuantities[ticket.ticketTypeId],
        price: ticket.price?.amount || 0,
        taxes: roomTaxesFromDiscounted(ticket.price?.amount || 0)
      }));
  };

  // Handle continue button click
  const handleContinue = () => {
    if (!hasSelectedTickets()) {
      alert('Please select at least one ticket to continue.');
      return;
    }

    if (!isAuthenticated) {
      // Open login modal if not authenticated
      setShowLoginModal(true);
      return;
    }

    const selectedTickets = getSelectedTickets();
    const pricing = calculateTotalPrice();
    
    // Prepare booking data
    const bookingData = {
      eventDetails: {
        id: event?.id || event?.eventId,
        title: event?.title,
        category: event?.category,
        location: event?.location || event?.city,
        venue: event?.venue,
        image: event?.images?.[0] || event?.image,
        latitude: event?.latitude || null,
        longitude: event?.longitude || null,
        address: event?.address || null,
        city: event?.city || null,
        state: event?.state || null
      },
      showDetails: {
        id: selectedShow?.showId,
        date: formatSelectedDate(),
        time: selectedShow?.time,
        duration: event?.duration ? `${Math.floor(event.duration / 60)}h ${event.duration % 60}m` : '2 hours'
      },
      selectedTickets,
      pricing,
      user: {
        name: user?.name,
        email: user?.email,
        mobile: user?.mobile
      }
    };

    navigate('/event-booking-review', {
      state: {
        bookingData,
        fromTicketSelection: true,
        eventId: event?.id || event?.eventId
      }
    });
    onRequestClose?.();
  };

  // Format selected date for display
  const formatSelectedDate = () => {
    if (!selectedDate?.date) return 'Date not selected';
    
    const date = new Date(selectedDate.date);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  const stateShell = (inner) => (
    <div
      className={
        isModal
          ? 'flex min-h-[200px] items-center justify-center rounded-2xl bg-[#FAF7F2] p-10'
          : 'flex min-h-screen items-center justify-center bg-[#FAF7F2]'
      }
    >
      {inner}
    </div>
  );

  // Loading state
  if (loading) {
    return stateShell(
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-black" />
    );
  }

  // Error state - no event selected
  if (!loading && !event && !resolvedEventId) {
    return stateShell(
      <div className="max-w-md p-4 text-center">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🎫</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Event Selected</h2>
          <p className="text-gray-600 mb-4">
            Please select an event from the events page to book tickets.
          </p>
          <button
            type="button"
            onClick={() => navigate('/events')}
            className="rounded-lg bg-black px-6 py-3 text-white transition-colors hover:bg-gray-800"
          >
            Browse Events
          </button>
        </div>
      </div>
    );
  }

  // Error state - failed to load
  if (error && (!event || availableDates.length === 0)) {
    return stateShell(
      <div className="max-w-md p-4 text-center">
        <div className="text-red-500 text-6xl mb-4">❌</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Event Data</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <p className="text-sm text-gray-500 mb-4">
          {availableDates.length === 0 && event
            ? "This event doesn't have any shows scheduled yet. Please check back later or contact the event organizer."
            : 'Please make sure the event exists and has shows scheduled.'}
        </p>
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full rounded-lg bg-black px-6 py-3 text-white transition-colors hover:bg-gray-800"
          >
            Try Again
          </button>
          <button
            type="button"
            onClick={() => navigate('/events')}
            className="w-full rounded-lg bg-gray-200 px-6 py-3 text-gray-900 transition-colors hover:bg-gray-300"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  // No data available state
  if (!loading && (!availableDates || availableDates.length === 0)) {
    return stateShell(
      <div className="max-w-md p-4 text-center">
        <div className="text-6xl mb-4">🎫</div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">No Shows Available</h2>
        <p className="text-gray-600 mb-4">
          This event does not have any shows scheduled at the moment.
        </p>
        <button
          type="button"
          onClick={() => navigate('/events')}
          className="rounded-lg bg-black px-6 py-3 text-white transition-colors hover:bg-gray-800"
        >
          Browse Other Events
        </button>
      </div>
    );
  }

  return (
    <div
      className={
        isModal
          ? 'flex max-h-[min(92vh,920px)] w-full max-w-[720px] flex-col overflow-hidden rounded-[20px] bg-[#FAF7F2] shadow-[0_25px_80px_rgba(0,0,0,0.18)] ring-1 ring-black/5'
          : 'relative flex min-h-screen flex-col bg-[#FAF7F2]'
      }
    >
      {/* Header */}
      <div
        className={
          isModal
            ? 'z-10 flex shrink-0 items-start justify-between gap-3 border-b border-[#E5E3DF] bg-[#FAF7F2] px-4 py-3 md:px-6 md:py-4'
            : 'sticky top-0 z-50 shrink-0 bg-[#faf7f2] p-[16px]'
        }
      >
        <div
          className={`grid w-full items-start gap-3 ${
            isModal && onRequestClose ? 'grid-cols-[auto_minmax(0,1fr)_auto]' : 'grid-cols-[auto_minmax(0,1fr)]'
          }`}
        >
          <button type="button" onClick={handleBack} className="shrink-0 pt-0.5" aria-label="Go back">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clipPath="url(#clip0_2846_5861)">
                <path d="M5 12H19" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12L11 18" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12L11 6" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </g>
              <defs>
                <clipPath id="clip0_2846_5861">
                  <rect width="24" height="24" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </button>
          <div className="flex min-w-0 flex-col gap-[6px]">
            <h1 className="text-[18px] font-medium leading-tight tracking-[-0.03em] text-black md:text-[20px]">
              Select tickets
            </h1>
            <div className="flex items-center justify-between text-sm text-[#78736A]">
              <div className="flex min-w-0 flex-wrap items-center gap-x-[12px] gap-y-1">
                {availableDates.length > 0 && (
                  <div className="flex shrink-0 items-center gap-[4px]">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <g clipPath="url(#clip0_2846_5871)">
                        <path d="M2.33301 4.08329C2.33301 3.77387 2.45592 3.47713 2.67472 3.25833C2.89351 3.03954 3.19026 2.91663 3.49967 2.91663H10.4997C10.8091 2.91663 11.1058 3.03954 11.3246 3.25833C11.5434 3.47713 11.6663 3.77387 11.6663 4.08329V11.0833C11.6663 11.3927 11.5434 11.6895 11.3246 11.9083C11.1058 12.127 10.8091 12.25 10.4997 12.25H3.49967C3.19026 12.25 2.89351 12.127 2.67472 11.9083C2.45592 11.6895 2.33301 11.3927 2.33301 11.0833V4.08329Z" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.33301 1.75V4.08333" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4.66699 1.75V4.08333" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2.33301 6.41663H11.6663" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4.66699 8.75H5.83366V9.91667H4.66699V8.75Z" stroke="#78736A" strokeLinecap="round" strokeLinejoin="round"/>
                      </g>
                      <defs>
                        <clipPath id="clip0_2846_5871">
                          <rect width="14" height="14" fill="white"/>
                        </clipPath>
                      </defs>
                    </svg>
                    <span className="text-[13px] leading-tight">
                      {(() => {
                        const firstDate = new Date(availableDates[0].date);
                        const lastDate = new Date(availableDates[availableDates.length - 1].date);
                        const firstDay = firstDate.toLocaleDateString('en-US', { weekday: 'short' });
                        const lastDay = lastDate.toLocaleDateString('en-US', { weekday: 'short' });
                        return availableDates.length === 1 ? firstDay : `${firstDay} - ${lastDay}`;
                      })()}
                    </span>
                  </div>
                )}
                {availableShows.length > 0 && (
                  <span className="text-[13px] leading-tight">{availableShows[0].time} onwards</span>
                )}
                {event?.location && (
                  <span className="min-w-0 truncate text-[13px] leading-tight">{event.location}</span>
                )}
              </div>
            </div>
          </div>
          {isModal && onRequestClose ? (
            <button
              type="button"
              onClick={onRequestClose}
              className="shrink-0 rounded-full p-2 text-black hover:bg-black/5"
              aria-label="Close"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          ) : null}
        </div>
      </div>

      <div className={isModal ? 'flex min-h-0 flex-1 flex-col overflow-hidden' : 'contents'}>
        {/* Date and Time Selection Card */}
        <div
          className={`mt-[20px] bg-white shadow-sm ${isModal ? 'mx-4 shrink-0 rounded-[16px] p-3 md:mx-6' : 'mx-[16px] shrink-0 rounded-[16px] p-[12px]'}`}
        >
        {/* Month Navigation */}
        <div className="flex items-center justify-between">
          <button className="">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M10.7076 12.0001L15.3076 16.6001L14.5996 17.3081L9.29162 12.0001L14.5996 6.6921L15.3076 7.4001L10.7076 12.0001Z" fill="#08192C"/>
            </svg>
          </button>
          <span className="text-[16px] font-regulr text-black">{selectedMonth}</span>
          <button className="">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M13.2924 12.0001L8.69238 16.6001L9.40038 17.3081L14.7084 12.0001L9.40038 6.6921L8.69238 7.4001L13.2924 12.0001Z" fill="#08192C"/>
            </svg>
          </button>
        </div>
        <div className='flex items-center justify-center my-[12px]'>
                  <svg width="100%" height="1px">
                    <line 
                      x1="0" y1="1" x2="100%" y2="1" 
                      stroke="#f0f0f0" 
                      strokeWidth="2" 
                      strokeDasharray="10 4"
                    />
                  </svg>
          </div>
        {/* Day Selection */}
        <div className="flex gap-[10px] mb-[12px] overflow-x-auto scrollbar-hide">
          {availableDates.map((dateInfo) => {
            const date = new Date(dateInfo.date);
            const dayObj = {
              day: date.toLocaleDateString('en-US', { weekday: 'short' }),
              date: date.getDate().toString(),
              month: date.toLocaleDateString('en-US', { month: 'short' }),
              fullDate: dateInfo.date
            };
            
            return (
              <button
                key={dateInfo.date}
                onClick={() => handleDateSelect(dateInfo)}
                className={`w-[60px] py-[6px] rounded-[12px] leading-none whitespace-nowrap flex flex-col items-center gap-[12px] ${
                  selectedDate?.date === dateInfo.date
                    ? 'bg-[#FF5E4C] text-white'
                    : 'text-[#636060]'
                }`}
              >
                <span className="text-[12px] font-medium leading-none">{dayObj.day}</span>
                <span className="text-[14px] font-bold leading-none">{dayObj.date}</span>
                <span className="text-[10px] font-medium leading-none">{dayObj.month}</span>
              </button>
            );
          })}
        </div>
        <div className='flex items-center justify-center my-[12px]'>
                  <svg width="100%" height="1px">
                    <line 
                      x1="0" y1="1" x2="100%" y2="1" 
                      stroke="#f0f0f0" 
                      strokeWidth="2" 
                      strokeDasharray="10 4"
                    />
                  </svg>
          </div>
        {/* Time Slot Selection */}
        <div className="grid grid-cols-4 gap-[8px]">
          {availableShows.map((show) => {
            const isSelected = selectedShow?.showId === show.showId;
            return (
              <button
                key={show.showId}
                onClick={() => handleShowSelect(show)}
                className={`flex items-center justify-center py-[8px] px-[4px] gap-[3px] border-[1px] border-[#E5E3DF] leading-none rounded-full text-[12px] tracking-[-0.7px] font-medium ${
                  isSelected
                    ? 'bg-[#FF5E4C] text-white'
                    : 'bg-white text-black'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
                  <g clipPath="url(#clip0_3257_137)">
                    <path d="M5.02905 1.84674C6.05658 1.84674 6.85406 2.91104 6.85425 4.1651C6.85425 5.41936 6.0567 6.48346 5.02905 6.48346C4.00142 6.48344 3.20386 5.41935 3.20386 4.1651C3.20405 2.91105 4.00154 1.84676 5.02905 1.84674ZM5.02905 2.53522C4.72845 2.53523 4.44663 2.70627 4.23608 3.00104C4.02576 3.29551 3.89242 3.70696 3.89233 4.1651C3.89233 4.62342 4.02569 5.03557 4.23608 5.33014C4.44663 5.62487 4.72846 5.79595 5.02905 5.79596C5.32964 5.79596 5.61147 5.62487 5.82202 5.33014C6.03242 5.03557 6.16577 4.62342 6.16577 4.1651C6.16568 3.70696 6.03235 3.29551 5.82202 3.00104C5.61147 2.70628 5.32966 2.53522 5.02905 2.53522Z" fill={isSelected ? "#FFFFFF" : "#E3E3E3"} stroke={isSelected ? "#FFFFFF" : "#E3E3E3"} strokeWidth="0.194444"/>
                    <path d="M2.06769 5.30231C2.8175 5.30257 3.39861 6.02811 3.39874 6.88043C3.39874 7.73288 2.81758 8.4583 2.06769 8.45856C1.3176 8.45856 0.735657 7.73303 0.735657 6.88043C0.735793 6.02796 1.31769 5.30231 2.06769 5.30231ZM2.06769 5.99078C1.90103 5.99078 1.7417 6.08132 1.6214 6.24176C1.50132 6.40211 1.42419 6.62773 1.42413 6.88043C1.42413 7.13339 1.50117 7.35965 1.6214 7.52008C1.7417 7.68052 1.90103 7.77106 2.06769 7.77106C2.2342 7.77091 2.3928 7.68039 2.513 7.52008C2.63327 7.35965 2.71027 7.13343 2.71027 6.88043C2.7102 6.62768 2.63312 6.40211 2.513 6.24176C2.3928 6.08145 2.2342 5.99093 2.06769 5.99078Z" fill={isSelected ? "#FFFFFF" : "#E3E3E3"} stroke={isSelected ? "#FFFFFF" : "#E3E3E3"} strokeWidth="0.194444"/>
                    <path d="M7.24954 6.53619C7.89978 6.53619 9.15164 6.80018 10.2427 7.46295C11.3367 8.12757 12.2827 9.20316 12.2827 10.8292C12.2827 11.6608 12.093 12.3053 11.6997 12.7422C11.3048 13.181 10.7195 13.3946 9.96439 13.3946C9.5116 13.3946 9.16528 13.2817 8.85794 13.1475L8.56107 13.0098C8.17099 12.8225 7.8172 12.6544 7.24954 12.6544C6.6822 12.6544 6.32912 12.8231 5.939 13.0098C5.55397 13.1946 5.13861 13.3946 4.5347 13.3946C3.77977 13.3945 3.19422 13.181 2.79935 12.7422C2.40624 12.3053 2.21634 11.6607 2.21634 10.8292C2.21642 9.20306 3.16329 8.12756 4.25735 7.46295C5.34822 6.8003 6.5992 6.53629 7.24954 6.53619ZM7.24954 7.22467C6.69772 7.22477 5.60484 7.4482 4.65189 8.01569C3.70154 8.58165 2.90489 9.47981 2.90482 10.8292C2.90482 11.5126 3.0332 11.9774 3.29056 12.2715C3.54513 12.5624 3.94384 12.706 4.5347 12.7061C4.97804 12.7061 5.27588 12.5649 5.64114 12.3897C6.04779 12.1944 6.5231 11.966 7.24954 11.9659C7.97618 11.9659 8.45221 12.1944 8.85892 12.3897C9.22401 12.5649 9.52128 12.7061 9.96439 12.7061C10.5556 12.7061 10.9549 12.5626 11.2095 12.2715C11.4669 11.9774 11.5952 11.5126 11.5952 10.8292C11.5952 9.47986 10.7985 8.58166 9.84818 8.01569C8.895 7.44803 7.80127 7.22467 7.24954 7.22467Z" fill={isSelected ? "#FFFFFF" : "#E3E3E3"} stroke={isSelected ? "#FFFFFF" : "#E3E3E3"} strokeWidth="0.194444"/>
                    <path d="M9.47098 1.84674C10.4985 1.84674 11.296 2.91104 11.2962 4.1651C11.2962 5.41936 10.4986 6.48346 9.47098 6.48346C8.44334 6.48344 7.64578 5.41935 7.64578 4.1651C7.64597 2.91105 8.44347 1.84676 9.47098 1.84674ZM9.47098 2.53522C9.17037 2.53523 8.88856 2.70627 8.67801 3.00104C8.46768 3.29551 8.33435 3.70696 8.33426 4.1651C8.33426 4.62342 8.46762 5.03557 8.67801 5.33014C8.88856 5.62487 9.17039 5.79595 9.47098 5.79596C9.77157 5.79596 10.0534 5.62487 10.2639 5.33014C10.4743 5.03557 10.6077 4.62342 10.6077 4.1651C10.6076 3.70696 10.4743 3.29551 10.2639 3.00104C10.0534 2.70628 9.77159 2.53522 9.47098 2.53522Z" fill={isSelected ? "#FFFFFF" : "#E3E3E3"} stroke={isSelected ? "#FFFFFF" : "#E3E3E3"} strokeWidth="0.194444"/>
                    <path d="M12.4328 5.30231C13.1826 5.30257 13.7637 6.02811 13.7639 6.88043C13.7639 7.73288 13.1827 8.4583 12.4328 8.45856C11.6827 8.45856 11.1008 7.73303 11.1008 6.88043C11.1009 6.02796 11.6828 5.30231 12.4328 5.30231ZM12.4328 5.99078C12.2661 5.99078 12.1068 6.08132 11.9865 6.24176C11.8664 6.40211 11.7893 6.62773 11.7892 6.88043C11.7892 7.13339 11.8663 7.35965 11.9865 7.52008C12.1068 7.68052 12.2661 7.77106 12.4328 7.77106C12.5993 7.77091 12.7579 7.68039 12.8781 7.52008C12.9984 7.35965 13.0754 7.13343 13.0754 6.88043C13.0753 6.62768 12.9982 6.40211 12.8781 6.24176C12.7579 6.08145 12.5993 5.99093 12.4328 5.99078Z" fill={isSelected ? "#FFFFFF" : "#E3E3E3"} stroke={isSelected ? "#FFFFFF" : "#E3E3E3"} strokeWidth="0.194444"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_3257_137">
                      <rect width="14" height="14" fill="white" transform="translate(0.25 0.777588)"/>
                    </clipPath>
                  </defs>
                </svg>
                {show.time}
              </button>
            );
          })}
        </div>
      </div>

      <div className={isModal ? 'min-h-0 flex-1 overflow-y-auto overscroll-y-contain' : ''}>
        {/* Ticket Types */}
        <div className={`mt-[16px] space-y-4 ${isModal ? 'px-4 pb-4 md:px-6' : 'px-[16px] pb-24'}`}>
        {ticketTypes.map((ticket) => (
          <div key={ticket.ticketTypeId} className="bg-white rounded-[16px] p-[12px] shadow-sm">
            <div className="flex flex-col justify-between items-start">
              <div className="flex-1">
                <h3 className="text-[18px] font-medium leading-tight text-black mb-[12px]">{ticket.name || ticket.title}</h3>
                <p className="text-[14px] text-[#8c8c8c] leading-[1.2] ">{ticket.description}</p>
              </div>
              <div className='flex w-full items-center justify-center my-[12px]'>
                  <svg width="100%" height="1px">
                    <line 
                      x1="0" y1="1" x2="100%" y2="1" 
                      stroke="#f0f0f0" 
                      strokeWidth="2" 
                      strokeDasharray="10 4"
                    />
                  </svg>
          </div>
              <div className="w-full flex items-center justify-between">
                <div>
              <div className="flex items-center gap-[3px]">
                  <span className="text-[14px] font-bold text-black">₹{ticket.price?.amount || 0}</span>
                </div>
                
                <p className="text-[12px] text-[#999999] font-medium">Includes ₹{roomTaxesFromDiscounted(ticket.price?.amount || 0)} taxes & fees</p>
                </div>
                 {(ticketQuantities[ticket.ticketTypeId] || 0) > 0 ? (
                    <div className="flex items-center justify-between w-[120px] h-[44px] px-[24px] rounded-[12px] border-[1px] border-[#FF5E4C]">
                    <button
                      onClick={() => handleRemoveTicket(ticket.ticketTypeId)}
                      className=""
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <g clip-path="url(#clip0_2956_479)">
                          <path d="M3.75 9H14.25" stroke="#FF5E4C" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_2956_479">
                            <rect width="18" height="18" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                     <span className="text-[16px] font-semibold text-[#6b6b6b] min-w-[20px] text-center">
                       {ticketQuantities[ticket.ticketTypeId] || 0}
                     </span>
                    <button
                      onClick={() => handleAddTicket(ticket.ticketTypeId)}
                      className=""
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <g clip-path="url(#clip0_2956_483)">
                          <path d="M9 3.75002V14.25" stroke="#FF5E4C" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M3.75 9H14.25" stroke="#FF5E4C" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/>
                        </g>
                        <defs>
                          <clipPath id="clip0_2956_483">
                            <rect width="18" height="18" fill="white"/>
                          </clipPath>
                        </defs>
                      </svg>
                    </button>
                  </div>
                   ) : (
                     <button
                       onClick={() => handleAddTicket(ticket.ticketTypeId)}
                       className="bg-black text-white h-[44px] w-[120px] rounded-[12px] text-[16px] px-[24px] font-semibold "
                     >
                       Add
                     </button>
                 )}
              </div>
            </div>
          </div>
        ))}
        </div>
      </div>

      {/* Bottom action bar — in-modal: docked; full page: fixed to viewport */}
      <div
        className={
          isModal
            ? 'shrink-0 border-t border-gray-200 bg-white px-4 py-4 md:px-6'
            : 'fixed bottom-0 left-0 right-0 border-t border-gray-200 bg-white p-[16px]'
        }
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-[14px] text-black">
              {(() => {
                const totalTickets = Object.values(ticketQuantities).reduce((sum, qty) => sum + qty, 0);
                return `${totalTickets} ticket${totalTickets !== 1 ? 's' : ''}`;
              })()}
            </p>
            <p className="text-[20px] font-bold text-black">₹{calculateTotalPrice().total.toLocaleString()}</p>
          </div>
          <button
            type="button"
            data-book-sniff="true"
            onClick={handleContinue}
            disabled={!hasSelectedTickets()}
            className={`rounded-xl px-8 py-3 font-medium transition-colors ${
              hasSelectedTickets()
                ? 'bg-black text-white hover:bg-gray-800'
                : 'cursor-not-allowed bg-gray-300 text-gray-500'
            }`}
          >
            Continue
          </button>
        </div>
      </div>
      </div>

      {/* Login Modal */}
      <LoginSignupModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => {
          setShowLoginModal(false);
          // After successful login, proceed with booking
          handleContinue();
        }}
      />
    </div>
  );
}
